'use client';

import { Link } from 'react-router-dom';
import { subscribeNewsletter } from '@/lib/clientApi';
import { Server, Shield, Globe, Clock, Database, ArrowRight, Check, Cpu, HardDrive, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { Suspense } from 'react';

export default function SharedHostingPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="flex flex-col min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-36 pb-40 md:pb-34 min-h-[80vh] flex items-center -mt-2">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[-1]"></div>
        
        {/* Server rack grid pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:4px_100%]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:100%_4px]"></div>
        </div>

        {/* Animated network connections */}
        <div className="absolute inset-0">
          <div className="absolute h-px w-full top-1/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse"></div>
          <div className="absolute h-px w-full top-2/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-300"></div>
          <div className="absolute h-px w-full top-3/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-700"></div>
        </div>

        {/* Data flow lines */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-px h-full left-1/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow"></div>
          <div className="absolute w-px h-full left-2/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-300"></div>
          <div className="absolute w-px h-full left-3/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-700"></div>
        </div>

        {/* Server status indicators */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
          <div className="absolute bottom-10 right-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
        </div>

        {/* Server icons with glow */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute left-[5%] top-1/4 group"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Server className="relative text-primary-400 w-16 h-16 transform group-hover:scale-110 transition-transform" />
          </motion.div>
          <motion.div 
            className="absolute right-[5%] bottom-1/4 group"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Database className="relative text-primary-400 w-12 h-12 transform group-hover:scale-110 transition-transform" />
          </motion.div>
          <motion.div 
            className="absolute right-[15%] top-1/3 group"
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Clock className="relative text-primary-400 w-20 h-20 transform group-hover:scale-110 transition-transform" />
          </motion.div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full">
            {Array.from({ length: 20 }).map((_, i) => {
              const delayClasses = ['delay-0', 'delay-100', 'delay-200', 'delay-300'];
              // Use deterministic pattern based on index
              const binaryValue = i % 2 === 0 ? '1' : '0';
              return (
                <div
                  key={i}
                  className={`absolute text-primary-500/10 text-xs animate-float-binary ${delayClasses[i % delayClasses.length]}`}
                  style={{
                    left: `${(i % 5) * 25}%`,
                    top: `${Math.floor(i / 5) * 25}%`
                  }}
                >
                  {binaryValue}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                  Starting at just $1/month
                </span>
              </motion.div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Shared Hosting</span>
              </h1>
              
              <p className="text-xl mb-8 text-slate-300">
                Reliable and affordable shared hosting solutions for your website, with unmetered bandwidth and 24/7 support.
              </p>

              <div className="flex flex-col sm:flex-row gap-5">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="#pricing"
                    className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                  >
                    View Plans <ArrowRight className="ml-2" />
                  </Link>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="bg-slate-800/80 text-primary-400 hover:bg-slate-700 border border-slate-700/50 px-8 py-4 rounded-lg font-medium text-center transition-all shadow-md backdrop-blur-sm inline-flex items-center justify-center"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Content - Server Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden md:block md:col-span-5"
            >
              <div className="relative">
                {/* Glow effects */}
                <motion.div
                  className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-tertiary-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute -bottom-10 -right-10 w-60 h-60 bg-gradient-to-br from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />

                {/* Server Illustration */}
                <motion.div
                  className="bg-slate-800/90 p-6 rounded-2xl shadow-3d border border-slate-700/50 backdrop-blur-sm"
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Status Box - Top */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-slate-900/90 p-3 rounded-lg border border-slate-700/50">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-xs text-slate-300">Secure Hosting</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                      <span className="text-xs text-slate-300">SSL Protected</span>
                    </div>
                    </div>

                  {/* Server Illustration Image */}
                  <div className="relative w-full aspect-square">
                    <img
                      src="/assets/illustrations/server-illustration.svg"
                      alt="Server Illustration"
                      className="absolute inset-0 w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </motion.div>

                {/* Status Badge */}
                <div className="absolute bottom-8 right-1/2 transform translate-x-1/2 translate-y-1/2 bg-gradient-rainbow text-white p-4 rounded-xl shadow-neon">
                  <div className="flex items-center">
                  <Shield className="h-6 w-6 mr-2" />
                  <span className="font-bold">99.9% Uptime</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Features
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">Shared Hosting</span>?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Our shared hosting plans are designed to provide reliable performance at an affordable price.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Server className="h-6 w-6" />,
                title: "High-Performance Servers",
                description: "Our servers are optimized for speed and reliability, ensuring your website loads quickly for visitors."
              },
              {
                icon: <Clock className="h-6 w-6" />,
                title: "Resource Allocation",
                description: "We carefully manage server resources to ensure your website has the CPU and memory it needs to perform well."
              },
              {
                icon: <Database className="h-6 w-6" />,
                title: "SSD Storage",
                description: "All our shared hosting plans come with fast SSD storage for improved performance and reliability."
              },
              {
                icon: <Globe className="h-6 w-6" />,
                title: "99.9% Uptime Guarantee",
                description: "We guarantee 99.9% uptime for your website, ensuring it's always available to your visitors."
              },
              {
                icon: <Shield className="h-6 w-6" />,
                title: "Enhanced Security",
                description: "Our shared hosting includes free SSL certificates, DDoS protection, and regular security updates."
              },
              {
                icon: <Server className="h-6 w-6" />,
                title: "24/7 Support",
                description: "Our technical support team is available 24/7 to help you with any issues or questions."
              }
            ].map((feature, index) => (
              <motion.div 
                key={feature.title}
                className="bg-slate-800/80 p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-700 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Pricing Plans
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Shared <span className="text-transparent bg-clip-text bg-gradient-rainbow">Hosting Plans</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Choose the perfect shared hosting plan for your website.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                name: "1 Dollar 1 Website",
                price: "$1",
                description: "Perfect for single website hosting",
                features: [
                  "1 Website",
                  "100GB SSD Web Space",
                  "Unmetered Bandwidth",
                  "5 × MySQL Database",
                  "10 x Email Accounts",
                  "5GB/Email id Storage",
                  "Free Wildcard SSL Certificates",
                  "Malware Scanning",
                  "DDoS Protection",
                  "High-Frequency Compute",
                  "Free CDN & Pre-cache",
                  "Autoscaled PHP Workers",
                  "WAF & Malware scans"
                ],
                popular: true
              },
              {
                name: "3 Dollar 5 Websites",
                price: "$3",
                description: "Great for multiple websites",
                features: [
                  "5 Website",
                  "300GB SSD Web Space",
                  "Unmetered Bandwidth",
                  "15 × MySQL Database",
                  "30 x Email Accounts",
                  "5GB/Email id Storage",
                  "Free Wildcard SSL Certificates",
                  "Malware Scanning",
                  "DDoS Protection",
                  "High-Frequency Compute",
                  "Free CDN & Pre-cache",
                  "Autoscaled PHP Workers",
                  "WAF & Malware scans"
                ],
                popular: false
              },
              {
                name: "5 Dollar 10 Websites",
                price: "$5",
                description: "Perfect for growing businesses",
                features: [
                  "10 Website",
                  "1TB SSD Web Space",
                  "Unmetered Bandwidth",
                  "50 × MySQL Database",
                  "100 x Email Accounts",
                  "5GB/Email id Storage",
                  "Free Wildcard SSL Certificates",
                  "Malware Scanning",
                  "DDoS Protection",
                  "High-Frequency Compute",
                  "Free CDN & Pre-cache",
                  "Autoscaled PHP Workers",
                  "WAF & Malware scans"
                ],
                popular: false
              },
              {
                name: "10 Dollar Everything Unlimited",
                price: "$10",
                description: "Ultimate unlimited hosting solution",
                features: [
                  "Unlimited Website",
                  "Unlimited SSD Web Space",
                  "Unmetered Bandwidth",
                  "Unlimited MySQL Database",
                  "Unlimited Email Accounts",
                  "10GB/Email id Storage",
                  "Free Wildcard SSL Certificates",
                  "Malware Scanning",
                  "DDoS Protection",
                  "High-Frequency Compute",
                  "Free CDN & Pre-cache",
                  "Autoscaled PHP Workers",
                  "WAF & Malware scans"
                ],
                popular: true
              }
            ].map((plan) => (
              <motion.div 
                key={plan.name}
                className={`bg-slate-800/80 rounded-xl shadow-xl border ${plan.popular ? 'border-primary-500 scale-105' : 'border-slate-700'} relative group backdrop-blur-sm overflow-hidden`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -10 }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-rainbow text-white text-xs font-bold px-3 py-1 rounded-tr-xl rounded-bl-xl">
                    POPULAR
                  </div>
                )}
                <div className="p-6 border-b border-slate-700">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-rainbow">{plan.price}</span>
                    <span className="text-slate-400 ml-1">/month</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-2">{plan.description}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <motion.div
                    className="mt-6"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to={plan.name === "1 Dollar 1 Website" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=1&product=1" :
                            plan.name === "3 Dollar 5 Websites" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=1&product=2" :
                            plan.name === "5 Dollar 10 Websites" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=1&product=3828" :
                            "https://panel.1dollarcloud.com/order.php?step=1&productGroup=1&product=3829"}
                      className={`block w-full ${plan.popular ? 'bg-gradient-rainbow' : 'bg-slate-700'} text-white text-center py-3 px-4 rounded-lg transition-all shadow-lg`}
                    >
                      Get Started
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Worldwide Data Servers Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Global Infrastructure
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Worldwide</span> Data Servers
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience lightning-fast loading times with our strategically positioned data centers across the globe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="bg-slate-800/80 p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Globe className="mr-3 h-6 w-6 text-primary-400" />
                  Global Network
                </h3>
                <p className="text-slate-300 mb-4">
                  Our 12 strategically located data centers ensure your website loads quickly for visitors anywhere in the world. With an average global latency of just 45ms, your users experience lightning-fast performance regardless of their location.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Redundant network connections with 99.99% uptime guarantee</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Automatic content distribution to the nearest data center</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Real-time traffic routing optimization for minimal latency</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">North America</h4>
                    <span className="text-green-400 text-sm font-medium">~18ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers covering US East, US West, and Canada</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Europe</h4>
                    <span className="text-green-400 text-sm font-medium">~22ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">4 data centers in UK, Germany, France, and Netherlands</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Asia Pacific</h4>
                    <span className="text-green-400 text-sm font-medium">~35ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers in Singapore, Japan, and Australia</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Other Regions</h4>
                    <span className="text-green-400 text-sm font-medium">~40ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">2 data centers in Brazil and South Africa</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#141e30] p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm relative overflow-hidden">
                {/* World Map */}
                <div className="relative h-[400px] w-full">
                  <div className="absolute inset-0">
          <img
                      src="/images/world-map.png"
                      alt="World Map"
                      width={1000}
                      height={500}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Server Locations */}
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(17% - 9px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30"></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US West
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(28% - 10px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US East
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(38% - 12px)', left: 'calc(54% - 18px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        UK
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(36% - 12px)', left: 'calc(20% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.9s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Canada
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(40% - 12px)', left: 'calc(60% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Germany
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(30% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.6s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Russia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(60% - 12px)', left: 'calc(69% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        India
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.8s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Singapore
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(86% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.1s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Japan
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(74% - 12px)', left: 'calc(84% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.5s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Australia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(33% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Brazil
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(77% - 12px)', left: 'calc(55% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        South Africa
                      </div>
                    </div>
                  </div>
                  
                  {/* Latency indicators */}
                  <div className="absolute bottom-4 right-4 bg-slate-900/80 p-3 rounded-lg border border-slate-700/50 backdrop-blur-sm">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <span className="text-xs text-slate-300">Low Latency (&lt;30ms)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <span className="text-xs text-slate-300">Medium Latency (30-60ms)</span>
                    </div>
                  </div>
                </div>
                
                {/* Overlay glow effects */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/10 to-tertiary-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-secondary-500/10 to-primary-500/10 rounded-full blur-3xl"></div>
              </div>
              
              <div className="absolute -bottom-5 -right-5 bg-gradient-rainbow text-white p-4 rounded-xl shadow-neon">
                <div className="flex items-center">
                  <Clock className="h-6 w-6 mr-2" />
                  <span className="font-bold">45ms Avg. Global Latency</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose 1 Dollar Cloud Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_500px_at_50%_50%,rgba(63,94,251,0.1),transparent)]"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_400px_at_70%_30%,rgba(37,249,245,0.1),transparent)]"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Why Choose Us
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud</span> is Your Best Choice
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience enterprise-grade hosting features at an unbeatable price point
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Unbeatable Value",
                description: "Starting at just $1/month, get premium hosting features that typically cost 10x more. We believe quality hosting shouldn't break the bank.",
                icon: <Server className="h-6 w-6" />,
                benefits: [
                  "Premium SSD Storage",
                  "Free SSL Certificate",
                  "Unlimited Bandwidth",
                  "24/7 Support Access"
                ]
              },
              {
                title: "Enterprise-Grade Security",
                description: "Your website's security is our top priority. We implement multiple layers of protection to keep your data safe.",
                icon: <Shield className="h-6 w-6" />,
                benefits: [
                  "DDoS Protection",
                  "Daily Malware Scans",
                  "Web Application Firewall",
                  "Real-time Threat Detection"
                ]
              },
              {
                title: "Lightning Fast Performance",
                description: "Experience blazing-fast load times with our optimized server infrastructure and cutting-edge technology.",
                icon: <Clock className="h-6 w-6" />,
                benefits: [
                  "NVMe SSD Storage",
                  "PHP OPcache",
                  "Nginx Web Server",
                  "Built-in Caching"
                ]
              },
              {
                title: "Reliability & Uptime",
                description: "Count on our 99.9% uptime guarantee. Your website stays online, keeping your business running 24/7.",
                icon: <Globe className="h-6 w-6" />,
                benefits: [
                  "99.9% Uptime Guarantee",
                  "Server Monitoring",
                  "Automated Backups",
                  "Redundant Network"
                ]
              },
              {
                title: "Easy Management",
                description: "User-friendly control panel and tools make it simple to manage your website, even for beginners.",
                icon: <Database className="h-6 w-6" />,
                benefits: [
                  "Modern Control Panel",
                  "One-Click Installers",
                  "Easy File Manager",
                  "Email Management"
                ]
              },
              {
                title: "Growth Ready",
                description: "Start small and scale as you grow. Our platform grows with your business needs.",
                icon: <ArrowRight className="h-6 w-6" />,
                benefits: [
                  "Scalable Resources",
                  "Easy Plan Upgrades",
                  "Multiple PHP Versions",
                  "Development Tools"
                ]
              }
            ].map((benefit, index) => (
              <motion.div 
                key={benefit.title}
                className="bg-slate-800/80 rounded-xl p-6 border border-slate-700/50 backdrop-blur-sm group hover:border-primary-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-300 mb-4">{benefit.description}</p>
                <ul className="space-y-2">
                  {benefit.benefits.map((item, i) => (
                    <li key={i} className="flex items-center text-sm group/item">
                      <div className="h-4 w-4 mr-2 flex-shrink-0 flex items-center justify-center">
                        <Check className="text-green-500 group-hover/item:scale-110 transition-transform" />
                      </div>
                      <span className="text-slate-300 group-hover/item:text-slate-200 transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p className="text-lg text-slate-300 mb-6">
              Join thousands of satisfied customers who trust 1 Dollar Cloud for their hosting needs
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link
                to="#pricing"
                className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
              >
                Get Started Now <ArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* One-Click Apps Section */}
      <section className="py-24 bg-slate-900/50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:4px_100%]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:100%_4px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4 text-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">100+ One-Click App Installations</span>
            </motion.h2>
            <motion.p 
              className="text-lg text-slate-300 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Deploy your favorite applications instantly with our automated installation system. No technical expertise required.
            </motion.p>
          </div>

          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {[
              { name: 'WordPress', logo: '/applogo/WordPress.svg' },
              { name: 'Joomla', logo: '/applogo/Joomla.svg' },
              { name: 'Drupal', logo: '/applogo/Drupal.svg' },
              { name: 'PrestaShop', logo: '/applogo/PrestaShop.svg' },
              { name: 'Magento', logo: '/applogo/Magento.svg' },
              { name: 'OpenCart', logo: '/applogo/OpenCart.svg' },
              { name: 'Laravel', logo: '/applogo/Laravel.svg' },
              { name: 'Node.JS', logo: '/applogo/Node.JS.svg' },
              { name: 'Django', logo: '/applogo/Django.svg' },
              { name: 'MediaWiki', logo: '/applogo/MediaWiki.svg' },
              { name: 'Moodle', logo: '/applogo/Moodle.svg' },
              { name: 'phpBB', logo: '/applogo/phpBB.svg' },
              { name: 'SuiteCRM', logo: '/applogo/SuiteCRM.svg' },
              { name: 'concrete5', logo: '/applogo/concrete5.svg' },
              { name: 'Matomo', logo: '/applogo/Matomo.svg' },
              { name: 'AbanteCart', logo: '/applogo/AbanteCart.svg' },
              { name: 'AgoraCart', logo: '/applogo/AgoraCart.svg' },
              { name: 'BuddyPress', logo: '/applogo/BuddyPress.svg' },
              { name: 'CakePHP', logo: '/applogo/CakePHP.svg' },
              { name: 'CMS Made Simple', logo: '/applogo/CMS Made Simple.svg' },
              { name: 'CubeCart', logo: '/applogo/CubeCart.svg' },
              { name: 'Dolibarr', logo: '/applogo/Dolibarr.svg' },
              { name: 'E107', logo: '/applogo/E107.svg' },
              { name: 'Elgg', logo: '/applogo/Elgg.svg' },
              { name: 'ExpressionEngine', logo: '/applogo/ExpressionEngine.svg' },
              { name: 'Flarum', logo: '/applogo/Flarum.svg' },
              { name: 'FluxBB', logo: '/applogo/FluxBB.svg' },
              { name: 'Form Tools', logo: '/applogo/Form Tools.svg' },
              { name: 'Forma LMS', logo: '/applogo/Forma LMS.svg' },
              { name: 'FreshRSS', logo: '/applogo/FreshRSS.svg' },
              { name: 'Geeklog', logo: '/applogo/Geeklog.svg' },
              { name: 'Gibbon', logo: '/applogo/Gibbon.svg' },
              { name: 'InvoiceNinja', logo: '/applogo/InvoiceNinja.svg' },
              { name: 'InvoicePlane', logo: '/applogo/InvoicePlane.svg' },
              { name: 'Kanboard', logo: '/applogo/Kanboard.svg' },
              { name: 'LimeSurvey', logo: '/applogo/LimeSurvey.svg' },
              { name: 'MODX', logo: '/applogo/MODX.svg' },
              { name: 'MamboCMS', logo: '/applogo/MamboCMS.svg' },
              { name: 'MantisBT', logo: '/applogo/MantisBT.svg' },
              { name: 'MyBB', logo: '/applogo/MyBB.svg' },
              { name: 'Nucleus', logo: '/applogo/Nucleus.svg' },
              { name: 'Open Web Analytics', logo: '/applogo/Open Web Analytics.svg' },
              { name: 'PHP-Fusion', logo: '/applogo/PHP-Fusion.svg' },
              { name: 'PHProjekt', logo: '/applogo/PHProjekt.svg' },
              { name: 'Piwigo', logo: '/applogo/Piwigo.svg' },
              { name: 'ProcessWire', logo: '/applogo/ProcessWire.svg' },
              { name: 'Revive Adserver', logo: '/applogo/Revive Adserver.svg' },
              { name: 'Roundcube', logo: '/applogo/Roundcube.svg' },
              { name: 'Serendipity', logo: '/applogo/Serendipity.svg' },
              { name: 'SilverStripeCMS', logo: '/applogo/SilverStripeCMS.svg' },
              { name: 'Simple Machines Forum', logo: '/applogo/Simple Machines Forum.svg' },
              { name: 'Snipe-IT', logo: '/applogo/Snipe-IT.svg' },
              { name: 'SquirrelMail', logo: '/applogo/SquirrelMail.svg' },
              { name: 'Tcexam', logo: '/applogo/Tcexam.svg' },
              { name: 'Textpattern', logo: '/applogo/Textpattern.svg' },
              { name: 'Typo3', logo: '/applogo/Typo3.svg' },
              { name: 'Vtiger', logo: '/applogo/Vtiger.svg' },
              { name: 'WebCalendar', logo: '/applogo/WebCalendar.svg' },
              { name: 'Xerte', logo: '/applogo/Xerte.svg' },
              { name: 'Zen Cart', logo: '/applogo/Zen Cart.svg' },
              { name: 'Zen Photo', logo: '/applogo/Zen Photo.svg' },
              { name: 'b2evolution', logo: '/applogo/b2evolution.svg' },
              { name: 'dotProject', logo: '/applogo/dotProject.svg' },
              { name: 'osCommerce', logo: '/applogo/osCommerce.svg' },
              { name: 'osTicket', logo: '/applogo/osTicket.svg' },
              { name: 'phpCOIN', logo: '/applogo/phpCOIN.svg' },
              { name: 'phpFormGenerator', logo: '/applogo/phpFormGenerator.svg' },
              { name: 'phpFreeChat', logo: '/applogo/phpFreeChat.svg' },
              { name: 'phpList', logo: '/applogo/phpList.svg' },
              { name: 'phpMyFAQ', logo: '/applogo/phpMyFAQ.svg' },
              { name: 'phpNuke', logo: '/applogo/phpNuke.svg' }
            ].map((app, index) => (
              <motion.div
                key={app.name}
                className="group relative bg-white rounded-xl p-2 hover:bg-slate-50 transition-all duration-300 border border-slate-200/50 backdrop-blur-sm"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ y: -3 }}
              >
                <div className="aspect-square relative mb-2 p-2">
                  <img
                    src={app.logo}
                    alt={`${app.name} logo`}
                    className="absolute inset-0 w-full h-full object-contain p-1 group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-xs font-medium text-slate-600 text-center group-hover:text-slate-900 transition-colors line-clamp-2">
                  {app.name}
                </h3>
              </motion.div>
            ))}
          </motion.div>

          {/* Removing the View All Applications button */}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background elements - Data center visualization */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Server visualization background */}
        <div className="absolute inset-0">
          {/* Connection lines */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`line-${i}`}
                className="absolute bg-primary-600/10 h-[1px]"
                style={{
                  top: `${5 + i * 5}%`,
                  left: '0',
                  right: '0',
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                }}
                transition={{
                  duration: 3 + (i % 5),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.1,
                }}
              />
            ))}
            
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`vline-${i}`}
                className="absolute bg-primary-600/10 w-[1px]"
                style={{
                  left: `${5 + i * 5}%`,
                  top: '0',
                  bottom: '0',
                }}
                animate={{
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4 + (i % 3),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Data nodes */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => {
              const size = 2 + (i % 3) * 2;
              return (
                <motion.div
                  key={`node-${i}`}
                  className="absolute rounded-full bg-primary-500/30"
                  style={{
                    width: size,
                    height: size,
                    top: `${10 + (i * 73) % 80}%`,
                    left: `${5 + (i * 57) % 90}%`,
                  }}
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + (i % 4),
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              );
            })}
          </div>
          
          {/* Data flow animations */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => {
              const isVertical = i % 2 === 0;
              const size = isVertical ? '1px' : '2px';
              const startPosition = `${10 + (i * 37) % 80}%`;
              
              return (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute bg-gradient-to-r from-primary-500/0 via-primary-500/50 to-primary-500/0"
                  style={{
                    width: isVertical ? size : '100px',
                    height: isVertical ? '50px' : size,
                    top: isVertical ? '0' : startPosition,
                    left: isVertical ? startPosition : '0',
                  }}
                  animate={{
                    [isVertical ? 'top' : 'left']: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3) * 2,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.2,
                  }}
                />
              );
            })}
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Get Started Today
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Ready to <span className="text-transparent bg-clip-text bg-gradient-rainbow">Get Started</span>?
              </h2>
              
              <p className="text-xl mb-8 text-slate-300">
                Choose the perfect hosting plan for your WordPress site and get online today.
              </p>
              
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/hosting"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                className="mt-12 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h4 className="text-xl font-semibold text-white mb-4">Subscribe to our newsletter</h4>
                <form onSubmit={async (e) => {
                   e.preventDefault();
                   const form = e.target as HTMLFormElement;
                   const email = (form.elements.namedItem('email') as HTMLInputElement).value;
                   let message = '';
                   let color = '';
                   if (email) {
                     const result = await subscribeNewsletter(email);
                     if (result.ok) {
                       message = result.message;
                        color = 'green';
                        (form.elements.namedItem('email') as HTMLInputElement).value = ''; // Clear the input field
                      } else {
                        message = result.message;
                        color = 'red';
                      }
                    } else {
                      message = 'Please enter your email address.';
                      color = 'red';
                    }
                    const subscribeMessage = document.getElementById('subscribe-message');
                    if (subscribeMessage) {
                      subscribeMessage.textContent = message;
                      subscribeMessage.style.color = color;
                    }
                  }}>
                   <div className="flex flex-col sm:flex-row">
                     <input
                       type="email"
                      name="email"
                      placeholder="Your email"
                      className="bg-slate-800/80 text-white rounded-l-lg sm:rounded-r-none rounded-r-lg sm:rounded-b-none rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full border border-slate-700 mb-2 sm:mb-0"
                    />
                    <motion.button
                      type="submit"
                      className="bg-gradient-rainbow text-white px-6 py-3 rounded-l-lg sm:rounded-l-none rounded-r-lg font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Send className="mr-2" />
                      <span>Subscribe</span>
                    </motion.button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    By subscribing, you agree to our Privacy Policy and consent to receive updates.
                  </p>
                  <p id="subscribe-message" className="mt-2 text-base text-center"></p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  </Suspense>
  );
}